/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import classNames from 'classnames';

import { Pages } from 'modules/navigation/domain/enums/pages';
import { selectTeamById } from 'modules/teams/store/teams/selectors';
import { useTeamById } from 'modules/teams/ui/hooks/use-team-by-id';
import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useParams } from 'react-router-dom';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import { useMainLayout } from 'ud-ui/layout/main/context';
import TeamEditFormTabs from '../components/team-view-tabs/tabs';
import TeamComponentsEditForm from './team-view-page';

const TeamsEditPage = () => {
  const { id } = useParams();
  const teamId = id ? parseInt(id) : 0;

  const teamInStore = useSelector(selectTeamById(teamId));
  const { data: team, isLoading } = useTeamById(teamId, {
    initialData: teamInStore,
  });

  const teamPlayersPage = useMatch(`${Pages.TEAMS.EDIT}/players`);
  const teamPlayerCreatePage = useMatch(`${Pages.TEAMS.EDIT}/players/create`);
  const teamParticipantsPage = useMatch(`${Pages.TEAMS.EDIT}/participants`);
  const teamWaitingListPage = useMatch(`${Pages.TEAMS.EDIT}/waiting-list`);
  const tabsRef = useRef<HTMLDivElement>(null);
  const { setHeaderContent } = useMainLayout();

  const playerId = Number(useLocation().state?.fromPlayer);
  const fromPlayer = team?.players?.find((player) => player.userId === playerId);

  const breadcrumbsItems = useMemo(() => {
    const fullName = fromPlayer?.lastName + ' ' + fromPlayer?.firstName + ' ' + (fromPlayer?.middleName ?? '');

    const items: { label: string; to: string; state?: any }[] = fromPlayer
      ? [
          { label: 'Игроки', to: Pages.PLAYERS.INDEX },
          {
            label: fullName ?? 'Неизвестный игрок',
            to: Pages.PLAYERS.builders.view(fromPlayer.userId),
          },
          { label: 'Команды', to: Pages.PLAYERS.builders.view(fromPlayer.userId, 'teams') },
        ]
      : [{ label: 'Команды', to: Pages.TEAMS.INDEX }];

    if (!team) {
      return items;
    }

    items.push({
      label: team.name,
      to: Pages.TEAMS.builders.edit(team.id),
      state: playerId && { fromPlayer: playerId },
    });

    if (teamPlayersPage || teamPlayerCreatePage) {
      items.push({
        label: 'Игроки',
        to: Pages.TEAMS.builders.edit(team.id, 'players'),
        state: playerId && { fromPlayer: playerId },
      });

      if (teamPlayerCreatePage) {
        items.push({
          label: 'Добавление игрока',
          to: Pages.TEAMS.builders.edit(team.id, 'players/create'),
          state: playerId && { fromPlayer: playerId },
        });
      }
    }

    if (teamParticipantsPage) {
      items.push({
        label: 'Участники',
        to: Pages.TEAMS.builders.edit(team.id, 'participants'),
      });
    }

    if (teamWaitingListPage) {
      items.push({
        label: 'Лист ожидания',
        to: Pages.TEAMS.builders.edit(team.id, 'waiting-list'),
      });
    }

    return items;
  }, [fromPlayer, playerId, team, teamPlayerCreatePage, teamPlayersPage, teamParticipantsPage, teamWaitingListPage]);

  const breadcrumbs = useMemo(() => {
    return team ? <UDBreadcrumbs className="my-10" breadcrumbs={breadcrumbsItems} /> : null;
  }, [breadcrumbsItems, team]);

  const tabsHeight = useMemo(() => {
    return tabsRef.current?.clientHeight ?? 62;
  }, [tabsRef]);

  useEffect(() => {
    setHeaderContent(breadcrumbs);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  return (
    <>
      <div
        className={classNames('flex w-full flex-col px-6 pt-6', {
          'h-full': !teamPlayersPage && !teamParticipantsPage && !teamWaitingListPage,
        })}
      >
        {team && (
          <>
            <div ref={tabsRef} className="pb-6">
              <TeamEditFormTabs state={{ fromPlayer: playerId }} />
            </div>

            <div
              css={css`
                height: calc(100% - ${tabsHeight}px);
                display: flex;
                flex-direction: column;
              `}
            >
              <TeamComponentsEditForm team={team} isLoading={isLoading} />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default TeamsEditPage;
